import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

interface SkipConfirmModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function SkipConfirmModal({
  isOpen,
  onCancel,
  onConfirm,
}: Readonly<SkipConfirmModalProps>) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-5 bg-black/55 backdrop-blur-[3px]"
      role="dialog"
      aria-modal="true"
      aria-label={t('hud.skipConfirmTitle')}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className="relative max-w-[400px] w-full overflow-hidden rounded-[18px] border !border-white/15 shadow-[0_24px_60px_rgba(0,0,0,0.55)]"
        style={{
          background:
            'linear-gradient(180deg, color-mix(in srgb, var(--game-eclipse, #205A52) 40%, var(--game-bg, #0D2320)), var(--game-bg, #0D2320))',
        }}
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/35 to-transparent" />

        <div className="flex flex-col p-5">
          <div className="!space-y-[6px] mb-[15px]">
            <h3 className="cardTitle title-transparent">{t('hud.skipConfirmTitle')}</h3>
            <p className="cardDescription">{t('hud.skipConfirmBody')}</p>
          </div>

          <div className="grid grid-cols-2 gap-[15px]">
            <button
              type="button"
              className="rounded-[14px] border !border-white/15 bg-white/5 backdrop-blur-[40px] px-4 py-3 text-center text-white transition hover:bg-white/10"
              onClick={onCancel}
            >
              {t('hud.skipConfirmCancel')}
            </button>
            <button
              type="button"
              className="rounded-[14px] border !border-white/15 bg-white/5 backdrop-blur-[40px] px-4 py-3 text-center text-white transition hover:bg-white/10"
              onClick={onConfirm}
            >
              {t('hud.skipConfirmYes')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
